import { computed, ref } from "vue";

export interface VisitingClubContext {
  districtId: string;
  divisionId?: string;
  areaId?: string;
  clubShortName?: string;
  clubName?: string;
  clubId?: number;
  isOnToastHost?: boolean;
}

const STORAGE_KEY = "toasthost:workspace-sidebar-visiting-club-context";

const currentContext = ref<VisitingClubContext | null>(null);
let syncedOwnerUserId: string | null | undefined;

interface StoredVisitingClubContext extends VisitingClubContext {
  ownerUserId: string | null;
}

function isValidContext(value: unknown): value is VisitingClubContext {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  const hasAnyClubField =
    candidate.clubShortName !== undefined ||
    candidate.clubName !== undefined ||
    candidate.clubId !== undefined ||
    candidate.isOnToastHost !== undefined;
  const hasValidClubFields =
    !hasAnyClubField ||
    (typeof candidate.clubShortName === "string" &&
      candidate.clubShortName.length > 0 &&
      typeof candidate.clubName === "string" &&
      candidate.clubName.length > 0 &&
      typeof candidate.clubId === "number" &&
      Number.isFinite(candidate.clubId) &&
      (candidate.isOnToastHost === undefined || typeof candidate.isOnToastHost === "boolean"));

  return (
    typeof candidate.districtId === "string" &&
    candidate.districtId.length > 0 &&
    (candidate.divisionId === undefined ||
      (typeof candidate.divisionId === "string" && candidate.divisionId.length > 0)) &&
    (candidate.areaId === undefined || (typeof candidate.areaId === "string" && candidate.areaId.length > 0)) &&
    (candidate.areaId === undefined || candidate.divisionId !== undefined) &&
    hasValidClubFields
  );
}

function isValidStoredContext(value: unknown): value is StoredVisitingClubContext {
  if (!isValidContext(value)) return false;
  const candidate = value as unknown as Record<string, unknown>;
  return candidate.ownerUserId === null || typeof candidate.ownerUserId === "string";
}

function syncAuthOwner(ownerUserId: string | null, authResolved: boolean) {
  if (!authResolved || typeof window === "undefined") return;
  if (syncedOwnerUserId === ownerUserId) return;

  syncedOwnerUserId = ownerUserId;
  currentContext.value = null;

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (isValidStoredContext(parsed) && parsed.ownerUserId === ownerUserId) {
      currentContext.value = {
        districtId: parsed.districtId,
        divisionId: parsed.divisionId,
        areaId: parsed.areaId,
        clubShortName: parsed.clubShortName,
        clubName: parsed.clubName,
        clubId: parsed.clubId,
        isOnToastHost: parsed.isOnToastHost,
      };
      return;
    }
  } catch {
    // Fall through to remove invalid state below.
  }

  currentContext.value = null;
  window.sessionStorage.removeItem(STORAGE_KEY);
}

function setContext(context: VisitingClubContext) {
  currentContext.value = context;
  if (typeof window !== "undefined" && syncedOwnerUserId !== undefined) {
    const storedContext: StoredVisitingClubContext = {
      ...context,
      ownerUserId: syncedOwnerUserId,
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storedContext));
  }
}

function clearContext() {
  currentContext.value = null;
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(STORAGE_KEY);
  }
}

export function useVisitingClubContext() {
  return {
    context: computed(() => currentContext.value),
    syncAuthOwner,
    setContext,
    clearContext,
  };
}
